// pages/respond/RespondPage.js
// Public answer page opened from an email button (mockups 4b-4e). The link's
// signed token names one message for one staff member; no sign-in needed.
// Nothing is saved on page load: only the Send button records an answer.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import api from '../../authentication/axiosInstance';
import { useMobileData } from '../mobile/mobileApi';
import styles from '../mobile/mobile.module.css';

export const LABELS = {
  dispatch: {
    title: 'Answer dispatch',
    confirm: 'Confirm',
    deny: "Can't make it",
    sendConfirm: "Send: I'll be there",
    sendDeny: "Send: I can't make it",
    doneConfirm: "You're confirmed.",
    doneDeny: "Dispatch knows you can't make it.",
  },
  warning: {
    title: 'Answer warning',
    confirm: "I'm available",
    deny: 'Not available',
    sendConfirm: "Send: I'm available",
    sendDeny: "Send: I'm not available",
    doneConfirm: "Dispatch knows you're available.",
    doneDeny: "Dispatch knows you're not available.",
  },
  stand_down: {
    title: 'Stand down',
    confirm: 'Got it',
    sendConfirm: 'Send: Got it',
    doneConfirm: 'Dispatch knows you got the stand-down.',
  },
  request: {
    title: 'Answer service request',
    confirm: 'Confirm',
    sendConfirm: "Send: I'll take it",
    doneConfirm: "You're confirmed.",
  },
};

const formatTime = (value) =>
  value
    ? new Date(value).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })
    : null;

const Rows = ({ page }) => (
  <div className={styles.card}>
    {page.rows.map((row) => (
      <div key={row.label} className={styles.detailRow}>
        <span>{row.label}</span>
        <span>{row.value}</span>
      </div>
    ))}
  </div>
);

const CallDispatch = ({ phone }) =>
  phone ? (
    <a className={styles.secondaryButton} href={`tel:${phone}`}>Call dispatch</a>
  ) : (
    <p className={styles.mutedText}>Call dispatch if something has changed.</p>
  );

const Shell = ({ title, children }) => (
  <main className={styles.page}>
    <header className={styles.pageHeader}>
      <h1 className={styles.pageTitle}>{title}</h1>
    </header>
    <div className={styles.content}>{children}</div>
  </main>
);

const RespondPage = () => {
  const { token } = useParams();
  const { search } = useLocation();
  const load = useCallback(() => api.get(`/mobile/links/${token}/`).then((response) => response.data), [token]);
  const { data: page, error: loadError, loading, replaceData } = useMobileData(load);
  const [savedAnswer, setSavedAnswer] = useState(null);
  const [openedToken, setOpenedToken] = useState(null);
  const [opening, setOpening] = useState(false);
  const currentToken = useRef(token);
  currentToken.current = token;
  const [answer, setAnswer] = useState(new URLSearchParams(search).get('answer') === 'deny' ? 'deny' : 'confirm');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSavedAnswer(null);
    setOpening(false);
    setNote('');
    setError(null);
    setAnswer(new URLSearchParams(search).get('answer') === 'deny' ? 'deny' : 'confirm');
  }, [token, search]);

  useEffect(() => {
    if (page?.allowed_answers?.length && !page.allowed_answers.includes(answer)) {
      setAnswer(page.allowed_answers[0]);
    }
  }, [page, answer]);

  const view = !page
    ? loading ? 'loading' : loadError?.response?.status === 404 ? 'invalid' : 'error'
    : page.answer
      ? savedAnswer && page.answer.value === savedAnswer.value && page.answer.at === savedAnswer.at ? 'done' : 'answered'
      : page.can_answer ? 'form' : 'closed';

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const response = await api.post(`/mobile/links/${token}/`, { answer, note: answer === 'deny' ? note : '' });
      replaceData(response.data);
      setSavedAnswer(response.data.answer);
    } catch (failure) {
      const data = failure?.response?.data;
      if (data?.page) {
        replaceData(data.page);
        setSavedAnswer(null);
        if (data.error !== 'already_answered') setError(data.error);
      } else {
        setError('Your answer was not sent. Check your connection and try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleOpen = async () => {
    setOpening(true);
    setError(null);
    try {
      const response = await api.post(`/mobile/links/${token}/`, { action: 'read' });
      if (currentToken.current === token) {
        replaceData(response.data);
        setOpenedToken(token);
      }
    } catch (failure) {
      if (currentToken.current === token) setError('Could not open the message. Check your connection and try again.');
    } finally {
      if (currentToken.current === token) setOpening(false);
    }
  };

  if (view === 'loading') return <Shell title="Facilitate"><p className={styles.loading}>Loading…</p></Shell>;
  if (view === 'invalid' || view === 'error') {
    return (
      <Shell title={view === 'invalid' ? "This link doesn't work" : 'Something went wrong'}>
        <p className={styles.bodyText}>
          {view === 'invalid'
            ? 'It may have been copied incompletely or be too old. Check the Facilitate app for your messages.'
            : 'Check your connection and open the link again.'}
        </p>
        <Link className={styles.primaryButton} to="/m">Open my messages</Link>
      </Shell>
    );
  }

  const labels = LABELS[page.kind] || LABELS.dispatch;
  const firstName = (page.staff_name || '').split(' ')[0];

  if (openedToken !== token) {
    return (
      <Shell title="Message from dispatch">
        <p className={styles.bodyText}>For {page.staff_name}. Tap below to view the message and let dispatch know you opened it.</p>
        {error && <p role="alert" className={styles.fieldError}>{error}</p>}
        <button type="button" className={styles.primaryButton} onClick={handleOpen} disabled={opening}>
          {opening ? 'Opening…' : 'View message'}
        </button>
        <p className={styles.mutedText}>Opening the message does not send an answer.</p>
      </Shell>
    );
  }

  if (view === 'done') {
    const done = page.answer?.value === 'deny' ? labels.doneDeny : labels.doneConfirm;
    return (
      <Shell title="Answer recorded">
        <p className={styles.bodyText}><strong>Thanks, {firstName}. {done}</strong></p>
        <Rows page={page} />
        <p className={styles.mutedText}>Need to change your answer later? Open it in the Facilitate app or call dispatch.</p>
        <Link className={styles.primaryButton} to={page.app_path}>Open in Facilitate</Link>
      </Shell>
    );
  }

  if (view === 'answered') {
    const value = page.answer.value === 'deny' ? labels.deny : labels.confirm;
    const at = formatTime(page.answer.at);
    return (
      <Shell title="You already answered">
        <p className={styles.bodyText}>
          Your answer is <strong>{value}</strong>{at ? `, sent at ${at}` : ''}. Nothing was changed.
        </p>
        <Rows page={page} />
        <Link className={styles.primaryButton} to={page.app_path}>Change it in Facilitate</Link>
        <CallDispatch phone={page.dispatch_phone} />
      </Shell>
    );
  }

  if (view === 'closed') {
    return (
      <Shell title="This link has expired">
        <p className={styles.bodyText}>{page.closed_reason || "This message can't be answered anymore."}</p>
        {error && <p role="alert" className={styles.fieldError}>{error}</p>}
        <Rows page={page} />
        <Link className={styles.primaryButton} to="/m">Open my messages</Link>
        <CallDispatch phone={page.dispatch_phone} />
      </Shell>
    );
  }

  const canDeny = page.allowed_answers.includes('deny');
  return (
    <Shell title={labels.title}>
      <p className={styles.mutedText}>Answering as {page.staff_name}</p>
      <Rows page={page} />
      {canDeny && (
        <div role="tablist" aria-label="Your answer" className={styles.segmented}>
          {['confirm', 'deny'].map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={answer === option}
              className={answer === option ? `${styles.segment} ${styles.segmentActive}` : styles.segment}
              onClick={() => setAnswer(option)}
            >
              {labels[option]}
            </button>
          ))}
        </div>
      )}
      {answer === 'deny' && (
        <label className={styles.fieldLabel}>
          Note for dispatch (optional)
          <textarea className={styles.textArea} value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
        </label>
      )}
      {error && <p role="alert" className={styles.fieldError}>{error}</p>}
      <button
        type="button"
        className={answer === 'deny' ? styles.dangerButton : styles.primaryButton}
        onClick={handleSend}
        disabled={sending}
      >
        {sending ? 'Sending…' : answer === 'deny' ? labels.sendDeny : labels.sendConfirm}
      </button>
      <p className={styles.mutedText} style={{ textAlign: 'center' }}>Nothing is sent until you tap the button.</p>
    </Shell>
  );
};

export default RespondPage;
